import React, { RefObject, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface PdfExportProps {
  targetRef: RefObject<HTMLElement>;
  filename?: string;
}

// PDF export using jsPDF and html2canvas for better React 19 compatibility
const PdfExport: React.FC<PdfExportProps> = ({ targetRef, filename = 'project-cost-analysis.pdf' }) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportToPdf = () => {
    if (!targetRef.current) {
      console.error('Target element not found for PDF export');
      return;
    }

    setIsExporting(true);

    html2canvas(targetRef.current, {
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    }).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
      setIsExporting(false);
    }).catch(error => {
      console.error('Error generating PDF:', error);
      setIsExporting(false);
    });
  };

  return (
    <div className="flex justify-end">
      <button
        onClick={handleExportToPdf}
        disabled={isExporting}
        className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isExporting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            Exporting...
          </>
        ) : (
          'Export to PDF'
        )}
      </button>
    </div>
  );
};

export default PdfExport;
